import React, { FC, useMemo } from 'react';
import { useTranslation } from '@dm/i18n';
import { useFela } from '../..';
import useThreshold from './threshold';
import Spinner from './Spinner';
import Snowflake from './seasonal/Snowflake';
import EasterEgg from './seasonal/EasterEgg';
import { WithTheme } from '../../Theme/utils/ThemeTypes';
import { WithId, WithDmId } from '../../utils/BaseProps';
import { isEaster, isWinter } from './seasonal/timeCheck';

export type LoadingProps = WithId &
  WithDmId & {
    /**
     * Beschreibung des Icons (Accessibility).
     */
    description?: string;
    /**
     * Boolean, der bestimmt, ob das Loading über dem gesamten Bildschirm dargestellt werden soll.
     */
    absolute?: boolean;
    /**
     * Größe des Loading Spinners.
     */
    size?: number;
    /**
     * Zeit in Millisekunden, um die Loading Animation zu verzögern.
     */
    threshold?: number;
    /**
     * Titel des Icons (Accessibility).
     */
    title?: string;
    /**
     * Saison-abhängiges Loading verwenden
     *
     * Dezember, Januar, Februar -> Schneeflocke
     *
     * Rest -> Standard
     */
    seasonal?: boolean;
  };

const loadingContainer = ({ theme, absolute }: WithTheme<LoadingProps>) => ({
  position: absolute ? 'fixed' : 'absolute', // to position element absolute to the body, we need position fixed
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  zIndex: theme.dimension.zIndex.layer + 1,
});

const spinnerBackdrop = ({ theme, absolute }: WithTheme<LoadingProps>) => ({
  left: 0,
  top: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  background: absolute ? 'rgba(255,255,255,.7)' : '',
  zIndex: theme.dimension.zIndex.layer + 2,
});

const selectLoading = (seasonal: boolean) => {
  if (seasonal && isWinter()) {
    return Snowflake;
  }
  if (seasonal && isEaster()) {
    return EasterEgg;
  }
  return Spinner;
};

const Loading: FC<LoadingProps> = ({
  absolute = false,
  size = 50,
  threshold = 100,
  title,
  dmId = 'dm-loading',
  id,
  description,
  seasonal = true,
}: LoadingProps) => {
  const hold = useThreshold(threshold);
  const { css } = useFela({ absolute });
  const [t] = useTranslation();

  const displayedTitle = title || t('ds.loading.title');

  const LoadingAnimation = useMemo(() => selectLoading(seasonal), [seasonal]);

  if (hold) {
    return null;
  }

  return (
    <div
      data-designsystem
      className={css(loadingContainer)}
      data-dmid="loading-container">
      <div className={css(spinnerBackdrop)}>
        <LoadingAnimation
          size={size}
          title={displayedTitle}
          description={description}
          dmId={dmId}
          id={id}
        />
      </div>
    </div>
  );
};

export default Loading;
